import React from "react";
import { Download } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

/**
 * Apple-styled "Export CSV" button — triggers a download for the given endpoint.
 *
 * Streams from the backend with the current Bearer token attached as a query param so
 * the browser can follow the redirect without losing authentication.
 *
 *   <ExportButton endpoint="/exports/revenue" range="monthly" testId="export-rev" />
 */
export default function ExportButton({ endpoint, range = "all_time", filename, testId = "export-btn", label = "Export CSV" }) {
  const handle = async () => {
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("equinox_token") : null;
      const url = `${API_BASE}${endpoint}?range=${encodeURIComponent(range)}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m?.[1] || filename || "export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  return (
    <button
      onClick={handle}
      data-testid={testId}
      className="px-3.5 py-2 rounded-full text-sm bg-white/[0.06] hover:bg-white/[0.10] text-white border border-white/[0.08] inline-flex items-center gap-2 transition"
      title="Download as CSV"
    >
      <Download className="w-3.5 h-3.5 text-white/60" />
      <span>{label}</span>
    </button>
  );
}
